import React from 'react';
import NeuralActivity from './NeuralActivity';

const AiProgress = ({ progressData, isLoading }) => {
  const {
    overallProgress = 0,
    currentTask = { icon: 'fas fa-hourglass-start', title: 'Pending...', description: 'Waiting for AI to start.', progress: 0, eta: 'N/A' },
    taskList = [],
    isThinking = false,
  } = progressData || {};

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <i className="fas fa-check-circle text-accent-green"></i>;
      case 'active': return <i className="fas fa-spinner fa-spin text-accent-purple"></i>;
      case 'pending': return <i className="far fa-circle text-gray-500"></i>;
      case 'error': return <i className="fas fa-exclamation-circle text-accent-red"></i>;
      default: return <i className="far fa-circle text-gray-500"></i>;
    }
  };
  
  const overallProgressWidth = isLoading && overallProgress === 0 ? '10%' : `${overallProgress}%`;


  return (
    <div className={`bg-dark-600 rounded-2xl p-6 h-full flex flex-col transform-gpu hover:scale-[1.005] transition-medium border border-dark-400 hover:border-accent-yellow/50 hover:shadow-glow-yellow group relative overflow-hidden`}>
      <div className="absolute -left-16 -bottom-16 w-48 h-48 bg-accent-yellow opacity-[0.03] rounded-full blur-3xl group-hover:opacity-[0.06] transition-opacity duration-300"></div>
      <h3 className="font-bold text-lg mb-5 flex items-center relative z-10">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-yellow/80 to-accent-orange/80 flex items-center justify-center mr-3 shadow-md group-hover:scale-110 transition-transform duration-300">
          <i className="fas fa-tasks text-white"></i>
        </div>
        AI Progress
      </h3>

      <div className="relative z-10 flex-grow flex flex-col">
        {/* Overall Progress */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-200 group-hover:text-white transition-colors">Overall Progress</span>
            <span className={`text-sm font-bold ${overallProgress > 70 ? 'text-accent-green' : overallProgress > 30 ? 'text-accent-yellow' : 'text-accent-orange'}`}>
              {isLoading && overallProgress === 0 ? 'Starting...' : `${overallProgress.toFixed(0)}%`}
            </span>
          </div>
          <div className="w-full bg-dark-700 rounded-full h-3 shadow-inner overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out ${isLoading && overallProgress === 0 ? 'animate-pulse' : ''}
                          ${overallProgress > 70 ? 'bg-gradient-to-r from-accent-green/70 to-accent-green' 
                          : overallProgress > 30 ? 'bg-gradient-to-r from-accent-yellow/70 to-accent-yellow' 
                          : 'bg-gradient-to-r from-accent-orange/70 to-accent-orange'}`}
              style={{ width: overallProgressWidth }}
            ></div>
          </div>
        </div>

        {/* Current Task */}
        <div className="bg-dark-500 rounded-xl p-4 mb-4 shadow-lg transition-all duration-300 hover:shadow-purple-500/20 hover:bg-dark-400/50">
          <h4 className="text-sm font-medium text-gray-200 mb-2 group-hover:text-white transition-colors">Current Task</h4>
          <div className="flex items-center mb-2">
            <div className={`w-10 h-10 rounded-lg bg-accent-purple/20 flex items-center justify-center mr-3 text-accent-purple text-lg`}>
              <i className={currentTask.icon || 'fas fa-microchip'}></i>
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-100 group-hover:text-white transition-colors">{currentTask.title}</div>
              <div className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors">{currentTask.description}</div>
            </div>
          </div>
          {currentTask.progress > 0 && (
            <>
              <div className="w-full bg-dark-700 rounded-full h-2 my-1 shadow-inner overflow-hidden">
                <div
                  className="bg-gradient-to-r from-accent-purple/70 to-accent-purple h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${currentTask.progress}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-400 flex justify-between">
                <span>Task Progress: {currentTask.progress}%</span>
                <span>ETA: {currentTask.eta}</span>
              </div>
            </>
          )}
        </div>

        {/* Task List */}
        <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 mb-4 min-h-[150px]">
          <h4 className="text-sm font-medium text-gray-200 mb-2 group-hover:text-white transition-colors sticky top-0 bg-dark-600 py-1">Analysis Plan</h4>
          <div className="space-y-2">
            {taskList.map((task) => (
              <div key={task.id} className={`bg-dark-500 rounded-lg p-3 border-l-4 shadow-md transition-all duration-300 hover:bg-dark-400/50
                ${task.status === 'completed' ? 'border-accent-green' : task.status === 'active' ? 'border-accent-purple animate-pulse-border-purple' : task.status === 'error' ? 'border-accent-red' : 'border-dark-400'}`}>
                <div className="flex items-center">
                  <div className="mr-3 text-lg">{getStatusIcon(task.status)}</div>
                  <div>
                    <div className="text-sm font-medium text-gray-200 group-hover:text-white transition-colors">{task.title}</div>
                    {task.description && <div className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors">{task.description}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* AI Neural Activity */}
        <div className="mt-auto pt-2 border-t border-dark-500">
            <div className="text-xs text-gray-400 mb-1 text-center group-hover:text-white transition-colors">AI Neural Activity</div>
            <div className="h-20 bg-dark-700/50 rounded-lg overflow-hidden shadow-inner">
                 <NeuralActivity isActive={isThinking || isLoading} />
            </div>
        </div>
      </div>
      <style jsx>{`
        .animate-pulse-border-purple {
            animation: pulse-border 1.5s infinite;
        }
        @keyframes pulse-border {
            0% { border-left-color: #9e3eff; }
            50% { border-left-color: #581c87; } /* darker purple */
            100% { border-left-color: #9e3eff; }
        }
      `}</style>
    </div>
  );
};

export default AiProgress;